import React, { createContext, Dispatch, FC, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';
import { components } from "@api/api";
import { currentCountrySelector, currentCurrencySelector } from '@selectors/countries.selector';
import { useRestaurantMenu } from "@vms/RestaurantMenu";
import { CustomRestaurantList } from "@vms/RestaurantMenu/RestaurantMenuContext";
import { Segment, segmentList } from "@models/Segment";
import Loader from "@components/Loader/Loader";

type TooltipItem = components['schemas']['Application.Common.Models.MainFilterOutput.Tooltip'];
type FilterWord = components['schemas']['MenuTrends.Application.Contracts.Models.FilterWord'];
type FilterItem = components["schemas"]["Application.Common.Models.MainFilterInput.FilterItem"];
type FilterItemExtended = components["schemas"]["Application.Common.Models.MainFilterOutput.FilterItemExtended"];
type FilterOutput = components["schemas"]["Application.Common.Models.MainFilterOutput.Filter"];
type FilterInput = components["schemas"]["Application.Common.Models.MainFilterInput.Filter"];
type IdNameItem = components["schemas"]["MenuTrends.Common.Models.IdNameItem"];
type RestaurantFilter = components["schemas"]["Application.Common.Models.MainFilterInput.RestaurantFilter"];
type OtherFilter = components["schemas"]["Application.Common.Models.MainFilterInput.OtherFilter"];
type MenuPartItem = components["schemas"]["Application.Common.Models.MainFilterInput.MenuPartItem"]

type MenuPartItemOutput = components["schemas"]["APIs.Controllers.Admin.Models.OutputFilterModel.MenuPartItem"] &
    { menuItemTypes?: FilterItemExtended[] | null };

type TagCategoryItem = { id: number; title: string };
type TagItem = { id: number; title: string; categoryId: number };

type FilterWithChecked<T> = (T & {
    isChecked: boolean;
})
type FiltersWithChecked<T> = FilterWithChecked<T>[]

type FilterItemWithChecked = FiltersWithChecked<FilterItem>

export type FilterValues = {
    venues: {
        segments: FilterItemWithChecked;
        regions: FilterItemWithChecked;
        cuisines: FiltersWithChecked<FilterItemExtended>;
        hostedLocations: FilterItemWithChecked;
        restaurants: FilterItem[];
        minUnits: number | null;
        maxUnits: number | null;
    };
    menuItems: {
        appetizers: FilterItemWithChecked;
        entrees: FilterItemWithChecked;
        sides: FilterItemWithChecked;
        desserts: FilterItemWithChecked;
        beverages: FilterItemWithChecked;
        words: FilterWord[];
        minPrice: number | null;
        maxPrice: number | null;
    };
    attributes: {
        dayParts: FilterItemWithChecked;
        ethnicSpeciality: FilterItemWithChecked;
        healthyTerms: boolean;
        smallPlates: boolean;
        kidMenus: boolean;
        glutenFree: boolean;
        plantBased: boolean;
        catering: boolean | null; // null = all items, true = catering only, false = regular only
        tags: TagItem[];
    };
};

export const defaultFilterValues: FilterValues = {
    venues: {
        segments: [],
        regions: [],
        cuisines: [],
        hostedLocations: [],
        restaurants: [],
        minUnits: null,
        maxUnits: null,
    },
    menuItems: {
        appetizers: [],
        entrees: [],
        sides: [],
        desserts: [],
        beverages: [],
        words: [],
        minPrice: null,
        maxPrice: null,
    },
    attributes: {
        dayParts: [],
        ethnicSpeciality: [],
        healthyTerms: false,
        smallPlates: false,
        kidMenus: false,
        glutenFree: false,
        plantBased: false,
        catering: false,
        tags: [],
    },
};

type SelectFiltersContextType = {
    filters: FilterValues;
    setPartial: Dispatch<Partial<FilterValues>>;
    resetFilters: () => void;
    isLoadingFilters: boolean;
    isFiltersPopupShown: boolean;
    openFiltersPopup: () => void;
    closeFiltersPopup: () => void;
    filtersText: string;
    countryCode: string;
    currentCurrency: string;
    restaurants: CustomRestaurantList[];
    tooltipsData: TooltipItem[];
    tags: TagItem[];
    tagCategories: TagCategoryItem[];
    keywords: FilterWord[];
}

export const defaultContextValue: SelectFiltersContextType = {
    filters: defaultFilterValues,
    setPartial: () => null,
    resetFilters: () => null,
    isLoadingFilters: true,
    isFiltersPopupShown: false,
    openFiltersPopup: () => null,
    closeFiltersPopup: () => null,
    filtersText: '',
    countryCode: '',
    currentCurrency: '',
    restaurants: [],
    tooltipsData: [],
    tags: [],
    tagCategories: [],
    keywords: [],
};

export const SelectFiltersContext = createContext<SelectFiltersContextType>(defaultContextValue);

const filterValuesReducer = (
    state: FilterValues,
    change: Partial<FilterValues>,
): FilterValues => {
    return {
        ...defaultFilterValues,
        ...state,
        ...change,
    };
};

export function useSelectFilters(
    initialData: FilterValues = defaultFilterValues
): [
    FilterValues,
    Dispatch<Partial<FilterValues>>
] {
    // @ts-ignore
    return useReducer(filterValuesReducer, initialData);
}

const addCheckedFlag = <T extends FilterItemExtended>(
    allItems?: T[] | null,
    selectedIds?: number[] | null,
    defaultChecked: boolean = true
): (T & { isChecked: boolean })[] => {
    return (allItems || []).map((item) => ({
        ...item,
        isChecked: selectedIds === undefined || selectedIds === null
            ? defaultChecked
            : selectedIds.includes(item.id!)
    }));
};

const extractIds = (items?: FilterItemExtended[] | null): number[] => {
    return (items ?? []).map(item => item.id!).filter(id => id !== undefined);
};


const getMenuItemTypes = (
    allMenuParts: MenuPartItemOutput[] | null | undefined,
    selectedMenuParts: MenuPartItemOutput[] | null | undefined,
    menuPartName: string,
    defaultChecked: boolean = true
): FiltersWithChecked<FilterItem> => {
    if (!allMenuParts) return [];
    const allMenuPart = allMenuParts.find((m) => m.name?.toLowerCase() === menuPartName);
    const selectedMenuPart = selectedMenuParts?.find((m) => m.name?.toLowerCase() === menuPartName);
    const selectedIds = selectedMenuParts ? extractIds(selectedMenuPart?.menuItemTypes) : undefined;
    return addCheckedFlag(allMenuPart?.menuItemTypes, selectedIds, defaultChecked);
};

export function useMapFiltersToClient() {
    return useCallback((
        allFiltersData?: FilterOutput,
        selectedFiltersData?: FilterInput,
        defaultChecked: boolean = true
    ): FilterValues => {
        if (!allFiltersData) return defaultFilterValues;

        const {
            restaurantFilters: allRestaurantFilters,
            menuPartFilterItems: allMenuParts,
            otherFilters: allOtherFilters
        } = allFiltersData;
        const {
            restaurantFilters: selectedRestaurantFilters,
            menuPartFilterItems: selectedMenuParts,
            otherFilters: selectedOtherFilters
        } = selectedFiltersData || {};

        const selectedSegmentIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.segments) : undefined;
        const selectedRegionIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.regions) : undefined;
        const selectedCuisineIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.cuisines) : undefined;
        const selectedHostedLocationIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.hostedLocations) : undefined;
        const selectedDayPartIds = selectedFiltersData ? extractIds(selectedRestaurantFilters?.dayParts) : undefined;

        return {
            venues: {
                segments: addCheckedFlag(allRestaurantFilters?.segments, selectedSegmentIds, defaultChecked)
                    .sort((a, b) => {
                        return segmentList.indexOf(a.name!.toLowerCase() as Segment) - segmentList.indexOf(b.name!.toLowerCase() as Segment);
                    }),
                regions: addCheckedFlag(allRestaurantFilters?.regions, selectedRegionIds, defaultChecked),
                cuisines: addCheckedFlag(allRestaurantFilters?.cuisines, selectedCuisineIds, defaultChecked),
                hostedLocations: addCheckedFlag(allRestaurantFilters?.hostedLocations, selectedHostedLocationIds, defaultChecked),
                restaurants: selectedOtherFilters?.restaurants || [],
                minUnits: selectedOtherFilters?.minUnits ?? allOtherFilters?.minUnits ?? 1,
                maxUnits: selectedOtherFilters?.maxUnits ?? allOtherFilters?.maxUnits ?? null,
            },
            menuItems: {
                appetizers: getMenuItemTypes(allMenuParts, selectedMenuParts, 'appetizer', defaultChecked),
                entrees: getMenuItemTypes(allMenuParts, selectedMenuParts, 'entree', defaultChecked),
                sides: getMenuItemTypes(allMenuParts, selectedMenuParts, 'side', defaultChecked),
                desserts: getMenuItemTypes(allMenuParts, selectedMenuParts, 'dessert', defaultChecked),
                beverages: getMenuItemTypes(allMenuParts, selectedMenuParts, 'beverage', defaultChecked),
                words: selectedOtherFilters?.customWords || [],
                minPrice: selectedOtherFilters?.minPrice ?? allOtherFilters?.minPrice ?? null,
                maxPrice: selectedOtherFilters?.maxPrice ?? allOtherFilters?.maxPrice ?? null,
            },
            attributes: {
                dayParts: addCheckedFlag(allRestaurantFilters?.dayParts, selectedDayPartIds, defaultChecked),
                ethnicSpeciality: [],
                healthyTerms: selectedOtherFilters?.healthyDining ?? allOtherFilters?.healthyDining ?? false,
                smallPlates: selectedOtherFilters?.smallPlates ?? allOtherFilters?.smallPlates ?? false,
                kidMenus: selectedOtherFilters?.kidMenus ?? allOtherFilters?.kidMenus ?? false,
                glutenFree: selectedOtherFilters?.glutenFree ?? allOtherFilters?.glutenFree ?? false,
                plantBased: selectedOtherFilters?.plantBased ?? allOtherFilters?.plantBased ?? false,
                catering: typeof selectedOtherFilters?.catering === 'boolean'
                    ? selectedOtherFilters.catering
                    : false,
                tags: [],
            },
        };
    }, []);

}

export function useMapFiltersToApi() {
    return useCallback((
        filters: FilterValues,
        countryCode: string,
        restaurantListId?: number,
        restaurantListName?: string
    ): FilterInput => {
        const { venues, menuItems, attributes } = filters;

        const extractChecked = <T extends FilterWithChecked<IdNameItem>>(
            items: T[]
        ): FilterItem[] => {
            return items
                .filter((item) => item.isChecked)
                .map(({ id, name }) => ({ id, name }));
        };

        const restaurantFilters: RestaurantFilter = {
            restaurantListId,
            restaurantListName,
            segments: extractChecked(venues.segments),
            regions: extractChecked(venues.regions),
            cuisines: extractChecked(venues.cuisines),
            hostedLocations: extractChecked(venues.hostedLocations),
            dayParts: extractChecked(attributes.dayParts),
        };

        const menuPartFilterItems: MenuPartItem[] = [
            { id: 1, name: 'Appetizer', menuItemTypes: extractChecked(menuItems.appetizers) },
            { id: 2, name: 'Entree', menuItemTypes: extractChecked(menuItems.entrees) },
            { id: 3, name: 'Side', menuItemTypes: extractChecked(menuItems.sides) },
            { id: 4, name: 'Dessert', menuItemTypes: extractChecked(menuItems.desserts) },
            { id: 5, name: 'Beverage', menuItemTypes: extractChecked(menuItems.beverages) },
        ];

        const otherFilters: OtherFilter = {
            healthyDining: attributes.healthyTerms,
            smallPlates: attributes.smallPlates,
            kidMenus: attributes.kidMenus,
            glutenFree: attributes.glutenFree,
            plantBased: attributes.plantBased,
            catering: attributes.catering,
            minPrice: menuItems.minPrice,
            maxPrice: menuItems.maxPrice,
            minUnits: venues.minUnits === 1 ? null : venues.minUnits,
            maxUnits: venues.maxUnits,
            restaurants: venues.restaurants,
            customWords: menuItems.words,
            tags: attributes.tags.map(({ id }) => id),
        };

        return {
            countryCode,
            restaurantFilters,
            menuPartFilterItems,
            otherFilters,
        };
    }, []);
}

export function useDefaultFilterData(): FilterOutput | undefined {
    const { rawFilterData, activeRestaurantListId } = useRestaurantMenu();
    return useMemo(() => {
        if (!rawFilterData?.filters?.length) return;

        const defaultListId = activeRestaurantListId || rawFilterData.defaultRestaurantListId;
        return (rawFilterData.filters.find(f => f.restaurantListIds?.includes(defaultListId!)) || rawFilterData.filters[0]);
    }, [rawFilterData, activeRestaurantListId]);
}

export function useMappedFiltersToClient() {
    const mapFiltersToClient = useMapFiltersToClient();
    const defaultFilterData = useDefaultFilterData();

    return useCallback(
        (selectedFiltersData?: FilterInput) => mapFiltersToClient(defaultFilterData, selectedFiltersData, !!selectedFiltersData),
        [mapFiltersToClient, defaultFilterData]
    );
}

export function useMappedFiltersToApi() {
    const mapFiltersToApi = useMapFiltersToApi();
    const { countryCode, filters } = useFilters();
    const { activeRestaurantListId, restaurantLists } = useRestaurantMenu();

    const activeRestaurantList = useMemo(
        () => restaurantLists.find(list => list.id === activeRestaurantListId),
        [restaurantLists, activeRestaurantListId]
    );

    return useMemo(
        () => mapFiltersToApi(filters, countryCode, activeRestaurantListId, activeRestaurantList?.name ?? ''),
        [mapFiltersToApi, filters, countryCode, activeRestaurantListId, activeRestaurantList]
    );
}

type FilterSection = 'venues' | 'menuItems' | 'attributes';
type CheckableItem = { id?: number; groupId?: number; isChecked: boolean };

export function useCheckboxGroupChange() {
    const { filters, setPartial } = useFilters();

    return useCallback((
        section: FilterSection,
        groupName: string,
        id: number | 'all',
        isGroup?: boolean
    ) => {
        const sectionData = filters[section];
        const group = (sectionData as Record<string, unknown>)[groupName] as CheckableItem[] | undefined;

        if (!group || !Array.isArray(group)) return;

        let updatedGroup: CheckableItem[];
        if (id === 'all') {
            const allChecked = group.every(item => item.isChecked);
            updatedGroup = group.map(item => ({ ...item, isChecked: !allChecked }));
        } else if (isGroup) {
            const groupItems = group.filter(item => item.groupId === id);
            const allGroupChecked = groupItems.every(item => item.isChecked);
            updatedGroup = group.map(item => ({
                ...item,
                isChecked: item.groupId === id ? !allGroupChecked : item.isChecked,
            }));
        } else {
            updatedGroup = group.map(item =>
                item.id === id ? { ...item, isChecked: !item.isChecked } : item
            );
        }

        setPartial({
            [section]: {
                ...sectionData,
                [groupName]: updatedGroup,
            },
        });
    }, [filters, setPartial]);
}

type FilterChanges = {
    isSomeFieldChanged: boolean;
    changedFieldsCount: number;
    lastChangedFieldName: string | null;
    changes: {
        venues: string[];
        menuItems: string[];
        attributes: string[];
    };
};

export function useGetFiltersChanges() {
    return useCallback((filters: FilterValues): FilterChanges => {
        let isSomeFieldChanged = false;
        let changedFieldsCount = 0;
        let lastChangedFieldName: string | null = null;
        const changes: FilterChanges['changes'] = {
            venues: [],
            menuItems: [],
            attributes: [],
        };

        const { venues, menuItems, attributes } = filters;

        if (!Object.keys(venues).length || !Object.keys(menuItems).length || !Object.keys(attributes).length) {
            return {
                isSomeFieldChanged,
                changedFieldsCount,
                lastChangedFieldName,
                changes,
            };
        }

        const {
            segments,
            hostedLocations,
            regions,
            cuisines,
            restaurants,
            minUnits,
            maxUnits,
        } = venues;

        const {
            appetizers,
            entrees,
            sides,
            desserts,
            beverages,
            words,
            minPrice,
            maxPrice,
        } = menuItems;

        const {
            dayParts,
            ethnicSpeciality,
            healthyTerms,
            smallPlates,
            kidMenus,
            glutenFree,
            plantBased,
            catering,
            tags,
        } = attributes;

        const optionChecked = (option: { isChecked: boolean }) => option.isChecked;
        const addCondition = (condition: boolean, fieldName: string, section: keyof FilterChanges['changes']) => {
            isSomeFieldChanged = isSomeFieldChanged || condition;
            if (condition) {
                changedFieldsCount++;
                lastChangedFieldName = fieldName;
                changes[section].push(fieldName);
            }
        };

        if (segments.length > 0) addCondition(segments.some(optionChecked), 'segments', 'venues');
        if (hostedLocations.length > 0) addCondition(hostedLocations.some(optionChecked), 'segments', 'venues');
        if (regions.length > 0) addCondition(regions.some(optionChecked), 'regions', 'venues');
        if (cuisines.length > 0) addCondition(cuisines.some(optionChecked), 'cuisines', 'venues');
        addCondition(restaurants.length > 0, 'restaurants', 'venues');
        addCondition(minUnits !== null && minUnits !== 1, 'minUnits', 'venues');
        addCondition(maxUnits !== null, 'maxUnits', 'venues');

        // Menu items changes
        if (appetizers.length > 0) addCondition(appetizers.some(optionChecked), 'appetizers', 'menuItems');
        if (entrees.length > 0) addCondition(entrees.some(optionChecked), 'entrees', 'menuItems');
        if (sides.length > 0) addCondition(sides.some(optionChecked), 'sides', 'menuItems');
        if (desserts.length > 0) addCondition(desserts.some(optionChecked), 'desserts', 'menuItems');
        if (beverages.length > 0) addCondition(beverages.some(optionChecked), 'beverages', 'menuItems');
        addCondition(words.length > 0, 'words', 'menuItems');
        addCondition(minPrice !== null, 'minPrice', 'menuItems');
        addCondition(maxPrice !== null, 'maxPrice', 'menuItems');

        // Attributes changes
        if (dayParts.length > 0) addCondition(dayParts.some(optionChecked), 'day parts', 'attributes');
        if (ethnicSpeciality.length > 0) addCondition(ethnicSpeciality.every(optionChecked), 'ethnic speciality', 'attributes');
        addCondition(healthyTerms, 'healthy terms', 'attributes');
        addCondition(smallPlates, 'small plates', 'attributes');
        addCondition(kidMenus, 'kid menus', 'attributes');
        addCondition(glutenFree, 'gluten free', 'attributes');
        addCondition(plantBased, 'plant based', 'attributes');
        addCondition(catering !== false, 'catering', 'attributes');
        addCondition(tags.length > 0, 'tags', 'attributes');

        return {
            isSomeFieldChanged,
            changedFieldsCount,
            lastChangedFieldName,
            changes,
        };
    }, []);
}

export function useFiltersText(filters: FilterValues): string {
    const getFiltersChanges = useGetFiltersChanges()
    const filtersChanges = getFiltersChanges(filters);

    return useMemo(() => {
        const { isSomeFieldChanged, changedFieldsCount, lastChangedFieldName } = filtersChanges;

        if (!isSomeFieldChanged) {
            return '';
        } else if (changedFieldsCount === 1) {
            return `your results are currently filtered by <b>${lastChangedFieldName}</b>`;
        } else {
            return 'your results have <b>multiple</b> filters applied';
        }
    }, [filtersChanges]);
}

const SelectFiltersProvider: FC = ({ children }) => {
    const countryCode = useSelector(currentCountrySelector);
    const currentCurrency = useSelector(currentCurrencySelector);

    const {
        activeRestaurantListId,
        customRestaurantLists,
        rawFilterData,
        isLoadingRestaurantLists: isLoadingFilters
    } = useRestaurantMenu();

    const mapFiltersToClient = useMappedFiltersToClient()
    const [filters, dispatchPartialFilters] = useSelectFilters();

    const [isFiltersPopupShown, setIsFiltersPopupShown] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const openFiltersPopup = useCallback(() => {
        setIsFiltersPopupShown(true);
    }, []);

    const closeFiltersPopup = useCallback(() => {
        setIsFiltersPopupShown(false);
    }, []);

    const initialFilterValues = mapFiltersToClient();

    const resetFilters = useCallback(() => {
        dispatchPartialFilters(initialFilterValues);
    }, [dispatchPartialFilters, initialFilterValues]);

    useEffect(() => {
        if (rawFilterData && !isInitialized) {
            dispatchPartialFilters(initialFilterValues);
            setIsInitialized(true);
        }
    }, [rawFilterData, isInitialized, initialFilterValues, dispatchPartialFilters]);

    useEffect(() => {
        if (isInitialized && activeRestaurantListId) {
            dispatchPartialFilters(initialFilterValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeRestaurantListId]);

    const tooltipsData: TooltipItem[] = useMemo(() => rawFilterData?.tooltips || [], [rawFilterData]);

    const { keywords, tags, tagCategories } = useMemo(() => {
        const words = rawFilterData?.words || [];
        const rawTagCategories = [...(rawFilterData?.tagCategories || [])];

        const keywordItems: FilterWord[] = words.map(word => ({
            ...word,
            wordExtensions: word.wordExtensions || [],
        }));

        const tagItems: TagItem[] = words
            .filter(word => word.isTag)
            .map(word => ({
                id: word.id!,
                title: word.name || '',
                categoryId: word.tagCategoryId || 0,
            }))
            .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));

        const categoryItems: TagCategoryItem[] = rawTagCategories
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(cat => ({
                id: cat.id!,
                title: cat.name || '',
            }))
            .filter(cat => tagItems.some(tag => tag.categoryId === cat.id));

        return { keywords: keywordItems, tags: tagItems, tagCategories: categoryItems };
    }, [rawFilterData]);

    const filtersText = useFiltersText(filters);

    const value = useMemo<SelectFiltersContextType>(() => ({
        filters,
        setPartial: dispatchPartialFilters,
        resetFilters,
        isLoadingFilters,
        isFiltersPopupShown,
        openFiltersPopup,
        closeFiltersPopup,
        filtersText,
        countryCode,
        currentCurrency,
        restaurants: customRestaurantLists,
        tooltipsData,
        tags,
        tagCategories,
        keywords,
    }), [
        filters,
        dispatchPartialFilters,
        resetFilters,
        isLoadingFilters,
        isFiltersPopupShown,
        openFiltersPopup,
        closeFiltersPopup,
        filtersText,
        countryCode,
        currentCurrency,
        customRestaurantLists,
        tooltipsData,
        tags,
        tagCategories,
        keywords,
    ]);

    if (isLoadingFilters && !isInitialized) {
        return (
            <Loader
                isShown
                noBg
            />
        );
    }

    return (
        <SelectFiltersContext.Provider value={value}>
            {children}
        </SelectFiltersContext.Provider>
    );
};

export const useFilters = () => {
    const context = useContext(SelectFiltersContext);
    if (!context) {
        throw new Error('useFilters must be used within SelectFiltersProvider');
    }
    return context;
};

export default SelectFiltersProvider;
